// src/context/AuthContext.js
import { createContext, useState, useEffect } from 'react';
import api from '../api/api';
import { excludeVisitor } from '../utils/analytics';

const AuthContext = createContext(null);

// Silenced in production — leaks the backend URL into every visitor's
// browser console, useful for debugging only.
// console.log("API Base URL:", process.env.REACT_APP_API_URL);

export const AuthProvider = ({ children }) => {
  // Read localStorage synchronously so the first paint already has auth state —
  // no blank-screen flicker waiting for a useEffect round-trip.
  const [user, setUser] = useState(() => {
    try {
      const info = localStorage.getItem('userInfo');
      return info ? JSON.parse(info) : null;
    } catch {
      try { localStorage.removeItem('userInfo'); } catch {}
      return null;
    }
  });

  // Automatically sync fresh permissions from database for employee accounts
  useEffect(() => {
    if (user?.role === 'employee' && (user?._id || user?.id)) {
      api.get('/employee/profile')
        .then(({ data }) => {
          if (data) {
            setUser(prev => {
              if (!prev) return prev;
              const updated = {
                ...prev,
                canUseAiBot: data.canUseAiBot || false,
                hasLeadsAccess: data.hasLeadsAccess || false,
                hasFinanceAccess: data.hasFinanceAccess || false,
                canBlog: data.canBlog || false,
                isManager: data.isManager || false,
                canCreateProjects: data.canCreateProjects || false
              };
              localStorage.setItem('userInfo', JSON.stringify(updated));
              return updated;
            });
          }
        })
        .catch(() => {});
    }
  }, [user?._id, user?.id, user?.role]);

    // For admin/HR logins we ask the browser for precise location first, so the
    // security audit records exactly where each attempt came from. Resolves to
    // null quickly if the user denies or ignores the prompt — login never hangs.
    // Turn precise GPS coords into a real city/state name (free, no key, CORS-ok),
    // so the audit shows the actual place instead of the unreliable IP city.
    const reverseGeocode = async (lat, lng) => {
      try {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), 3500);
        const r = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`, { signal: ctrl.signal });
        clearTimeout(t);
        const j = await r.json();
        return { city: j.city || j.locality || '', region: j.principalSubdivision || '', country: j.countryName || '' };
      } catch (_) { return {}; }
    };

    const getGeo = () => new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({ granted: false });
      let done = false;
      const finish = (v) => { if (!done) { done = true; resolve(v); } };
      const ok = async (pos) => {
        const base = { granted: true, lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
        const place = await reverseGeocode(base.lat, base.lng);
        finish({ ...base, ...place });
      };
      const timer = setTimeout(() => finish({ granted: false }), 10000);
      // Try a precise fix, reusing a recent one (up to 2 min old) so consecutive
      // login attempts don't each wait on a slow fresh GPS lock.
      navigator.geolocation.getCurrentPosition(
        (pos) => { clearTimeout(timer); ok(pos); },
        () => {
          // Fallback: faster, low-accuracy, accept a cached fix up to 5 min old.
          navigator.geolocation.getCurrentPosition(
            (pos) => { clearTimeout(timer); ok(pos); },
            () => { clearTimeout(timer); finish({ granted: false }); },
            { enableHighAccuracy: false, timeout: 6000, maximumAge: 300000 }
          );
        },
        { enableHighAccuracy: true, timeout: 7000, maximumAge: 120000 }
      );
    });

  const login = async (email, password, role, deviceInfo, extras) => {
    let url = '/auth/login/employee';
    if (role === 'hr' || role === 'admin') url = '/auth/login/hr';
    else if (role === 'partner') url = '/auth/login/partner';

    // Prepare request body - include deviceInfo and isTouchDevice for employee logins
    const requestBody = { email, password };
    if (role === 'employee' && deviceInfo) {
      requestBody.deviceInfo = deviceInfo;
      // Detect if device has touch capability
      requestBody.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    }
    // HR / Admin require an additional access code; the caller passes it
    // through `extras`. Employee logins never use this.
    if ((role === 'hr' || role === 'admin') && extras?.accessCode) {
      requestBody.accessCode = extras.accessCode;
    }
    // Attach precise location to admin/HR attempts for the security audit.
    if (role === 'hr' || role === 'admin') {
      requestBody.geo = await getGeo();
    }

    const { data } = await api.post(url, requestBody);
    if (data) {
      const actualRole = data.role || role;
      const userData = { ...data, role: actualRole };
      localStorage.setItem('userInfo', JSON.stringify(userData));
      setUser(userData);
      // This visitor is a company member — purge their anonymous browsing
      // history from public-traffic analytics.
      try { excludeVisitor(); } catch (_) {}
      return actualRole;
    }
  };

  // Google Sign-In (login only). Sends the Google credential (+ access code for
  // HR/Admin) to the backend, which only lets EXISTING accounts in. Throws on
  // failure so the caller can read err.response.data (noAccount / needAccessCode).
  const googleLogin = async (credential, accessCode) => {
    const { data } = await api.post('/auth/google', { credential, accessCode });
    if (data && data.token) {
      const actualRole = data.role;
      const userData = { ...data, role: actualRole };
      localStorage.setItem('userInfo', JSON.stringify(userData));
      setUser(userData);
      try { excludeVisitor(); } catch (_) {}
      return actualRole;
    }
    throw new Error('Google sign-in failed.');
  };

  // --- NEW OTP REGISTRATION FUNCTIONS ---
  const requestRegistrationOtp = async (userData) => {
    // This just sends the request, doesn't log the user in
    const { data } = await api.post('/auth/register/request-otp', userData);
    return data; // Return the success message
  };

  const registerHR = async ({ name, email, password }) => {
    const { data } = await api.post('/auth/register-hr', { name, email, password });
    localStorage.setItem('userInfo', JSON.stringify({ ...data, role: 'hr' }));
    setUser({ ...data, role: 'hr' });
  };

  const registerAdmin = async ({ name, email, password }) => {
    const { data } = await api.post('/auth/register-admin', { name, email, password });
    localStorage.setItem('userInfo', JSON.stringify({ ...data, role: 'admin' }));
    setUser({ ...data, role: 'admin' });
  };

  const verifyAndRegister = async (email, otp) => {
    const { data } = await api.post('/auth/register/verify', { email, otp });
    if (data) {
      const newUserData = { ...data, role: 'employee' };
      localStorage.setItem('userInfo', JSON.stringify(newUserData));
      setUser(newUserData);
    }
  };

  // --- NEW OTP PASSWORD RESET FUNCTIONS ---
  const forgotPassword = async (email) => {
    const { data } = await api.post('/auth/forgotpassword', { email });
    return data;
  };

  const resetPassword = async (email, otp, password) => {
    const { data } = await api.put('/auth/resetpassword', { email, otp, password });
    return data;
  };

  const logout = () => {
    localStorage.removeItem('userInfo');
    setUser(null);
  };

  const value = {
    user,
    setUser,
    login,
    googleLogin,
    logout,
    requestRegistrationOtp, // New
    verifyAndRegister,      // New
    forgotPassword,         // Updated
    resetPassword,          // Updated
    isAuthenticated: !!user,
    loading: false,
    registerHR,
    registerAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;


